// Role definitions
export const ROLES = {
    GUEST: 'guest',
    MEMBER: 'member', 
    TRAINER: 'trainer',
    BOARD: 'board',
    AUDITOR: 'auditor'
};

// Type definitions
interface User {
    mg_nr?: string;
    mitglied_nr?: string;
    vorstand?: number;
    trainer?: number;
    kassenpruefer?: number;
    roles?: Array<{
        name: string;
        description?: string;
        priority?: number;
    }>;
}

// Check if user has a specific role
export const hasRole = (user: User | null, roleName: string): boolean => {
    if (!user) return false;
    
    // New logic with Many-to-Many
    if (user?.roles && Array.isArray(user.roles)) {
        return user.roles.some(r => r.name === roleName);
    }
    
    // Fallback for transition phase
    switch(roleName) {
        case ROLES.GUEST:
            return user?.mitglied_nr === 'Gast';
        case ROLES.BOARD:
            return user?.vorstand === 1;
        case ROLES.TRAINER:
            return user?.trainer === 1;
        case ROLES.AUDITOR:
            return user?.kassenpruefer === 1;
        case ROLES.MEMBER:
            return user?.mitglied_nr !== 'Gast';
        default:
            return false;
    }
};

// Shortcuts for common checks
export const isGuest = (user: User | null) => hasRole(user, ROLES.GUEST);
export const isBoard = (user: User | null) => hasRole(user, ROLES.BOARD);
export const isTrainer = (user: User | null) => hasRole(user, ROLES.TRAINER);
export const isAuditor = (user: User | null) => hasRole(user, ROLES.AUDITOR);
export const isMember = (user: User | null) => hasRole(user, ROLES.MEMBER);

// Check if user has ANY of the specified roles
export const hasAnyRole = (user: User | null, roleNames: string[]): boolean => {
    return roleNames.some(roleName => hasRole(user, roleName));
};

// Check if user has ALL specified roles
export const hasAllRoles = (user: User | null, roleNames: string[]): boolean => {
    return roleNames.every(roleName => hasRole(user, roleName));
};

// Get primary role (highest priority)
export const getPrimaryRole = (user: User | null): string => {
    if (!user) return ROLES.MEMBER;
    
    if (user?.roles && Array.isArray(user.roles)) {
        // Sort by priority and take the first one
        const sorted = [...user.roles].sort((a, b) => (b.priority || 0) - (a.priority || 0));
        return sorted[0]?.name || ROLES.MEMBER;
    }
    
    // Fallback
    if (user?.vorstand === 1) return ROLES.BOARD;
    if (user?.trainer === 1) return ROLES.TRAINER;
    if (user?.kassenpruefer === 1) return ROLES.AUDITOR;
    if (user?.mitglied_nr === 'Gast') return ROLES.GUEST;
    return ROLES.MEMBER;
};

// Get all roles as display string
export const getRolesDisplay = (user: User | null): string => {
    if (!user) return 'Unbekannt';
    
    const roleMap: Record<string, string> = {
        'guest': 'Gast',
        'member': 'Mitglied',
        'trainer': 'Trainer',
        'board': 'Vorstand',
        'auditor': 'Kassenprüfer'
    };
    
    if (user?.roles && Array.isArray(user.roles)) {
        return user.roles
            .sort((a, b) => (b.priority || 0) - (a.priority || 0))
            .map(r => roleMap[r.name] || r.description || r.name)
            .join(', ');
    }
    
    // Fallback
    const roles = [];
    if (user?.mitglied_nr === 'Gast') return 'Gast';
    if (user?.vorstand === 1) roles.push('Vorstand');
    if (user?.trainer === 1) roles.push('Trainer');
    if (user?.kassenpruefer === 1) roles.push('Kassenprüfer');
    if (roles.length === 0 && user?.mitglied_nr !== 'Gast') roles.push('Mitglied');
    return roles.join(', ') || 'Unbekannt';
};

// Get role for badge display (most important role)
export const getRoleBadge = (user: User | null) => {
    const primary = getPrimaryRole(user);
    const roleMap: Record<string, { label: string; color: string }> = {
        'guest': { label: 'Gast', color: 'warning' },
        'member': { label: 'Mitglied', color: 'success' },
        'trainer': { label: 'Trainer', color: 'info' },
        'board': { label: 'Vorstand', color: 'primary' },
        'auditor': { label: 'Kassenprüfer', color: 'secondary' }
    };
    return roleMap[primary] || { label: 'Unbekannt', color: 'default' };
};

// Check if user has administration rights
export const hasAdminRights = (user: User | null): boolean => {
    return hasAnyRole(user, [ROLES.BOARD, ROLES.TRAINER]);
};

// Check if user has elevated rights
export const hasElevatedRights = (user: User | null): boolean => {
    return hasAnyRole(user, [ROLES.BOARD, ROLES.TRAINER, ROLES.AUDITOR]);
};